import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";

export default function AdminSalarySettlement() {
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [selectedBoyId, setSelectedBoyId] = useState("");
  const [summary, setSummary] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  const [payAmount, setPayAmount] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [loading, setLoading] = useState(false);

  /* ---------------- LOAD DELIVERY BOYS ---------------- */
const loadDeliveryBoys = async () => {
  try {
    const res = await axios.get("/admin/delivery-boys/");

    // ✅ handle any backend response shape safely
    if (Array.isArray(res.data)) {
      setDeliveryBoys(res.data);
    } else if (Array.isArray(res.data.delivery_boys)) {
      setDeliveryBoys(res.data.delivery_boys);
    } else if (Array.isArray(res.data.results)) {
      setDeliveryBoys(res.data.results);
    } else {
      setDeliveryBoys([]);
      console.error("Unexpected delivery boys response:", res.data);
    }
  } catch (err) {
    alert("Failed to load delivery boys");
    setDeliveryBoys([]);
  }
};

  /* ---------------- LOAD SUMMARY ---------------- */
  const loadSummary = async (boyId) => {
    if (!boyId) return;
    try {
      const res = await axios.get(
        `/admin/delivery-salary-summary/?delivery_boy_id=${boyId}`
      );
      setSummary(res.data);
    } catch {
      alert("Failed to load salary summary");
    }
  };

  /* ---------------- LOAD PAYMENT HISTORY ---------------- */
  const loadHistory = async (boyId) => {
    if (!boyId) return;
    try {
      const res = await axios.get(
        `/admin/delivery-payment-history/?delivery_boy_id=${boyId}`
      );
      setPaymentHistory(res.data);
    } catch {
      alert("Failed to load payment history");
    }
  };

  useEffect(() => {
    loadDeliveryBoys();
  }, []);

  useEffect(() => {
    if (selectedBoyId) {
      loadSummary(selectedBoyId);
      loadHistory(selectedBoyId);
    }
  }, [selectedBoyId]);

  /* ---------------- PAY DELIVERY BOY ---------------- */
  const payDeliveryBoy = async () => {
    if (!payAmount || payAmount <= 0) {
      alert("Enter valid amount");
      return;
    }

    try {
      setLoading(true);
      await axios.post("/admin/pay-delivery-boy/", {
        delivery_boy_id: selectedBoyId,
        amount: payAmount,
        note: adminNote,
      });

      alert("Payment recorded successfully");

      setPayAmount("");
      setAdminNote("");

      loadSummary(selectedBoyId);
      loadHistory(selectedBoyId);
    } catch {
      alert("Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-3">
      <h3>💼 Delivery Boy Payments</h3>

      {/* DELIVERY BOY DROPDOWN */}
      <select
        className="form-control mb-3"
        value={selectedBoyId}
        onChange={(e) => setSelectedBoyId(e.target.value)}
      >
        <option value="">-- Select Delivery Boy --</option>
        {deliveryBoys.map((boy) => (
          <option key={boy.id} value={boy.id}>
            {boy.name} ({boy.phone})
          </option>
        ))}
      </select>

      {/* SUMMARY */}
      {summary && (
        <div className="card p-3 mb-4">
          <h5>
            {summary.name} ({summary.phone})
          </h5>

          <p>🚚 Total Delivery Earning: ₹{summary.total_earning}</p>
          <p>💰 Total Paid: ₹{summary.total_paid}</p>
          <p className="fw-bold text-danger">
            Pending Amount: ₹{summary.pending_amount}
          </p>

          <hr />

          {/* PAY INPUT */}
          <label className="fw-bold">Pay Amount</label>
          <input
            type="number"
            className="form-control mb-2"
            placeholder="Enter amount to pay"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
          />

          <textarea
            className="form-control mb-2"
            placeholder="Admin note (optional)"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
          />

          <button
            className="btn btn-success"
            onClick={payDeliveryBoy}
            disabled={loading}
          >
            {loading ? "Processing..." : "Pay Delivery Boy"}
          </button>
        </div>
      )}

      {/* PAYMENT HISTORY */}
      {paymentHistory.length > 0 && (
        <div className="card p-3">
          <h5>📜 Payment History</h5>

          <table className="table table-bordered mt-2">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {paymentHistory.map((txn) => (
                <tr key={txn.id}>
                  <td>
                    {new Date(txn.date).toLocaleDateString()}{" "}
                    {new Date(txn.date).toLocaleTimeString()}
                  </td>
                  <td>₹{txn.amount}</td>
                  <td>{txn.note || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
